import { createInstance, dateReviver } from '@/api';
import {
    DeleteQualityCheckDto,
    DeliveryQualityCheckClient,
    DeliveryQualityCheckDetailDto,
    SaveBasicQualityCheckDto,
    SaveStandardQualityCheckDto,
    SaveStandardQualityCheckStatusDto,
    TestDto
} from '@/api/backoffice/clients';
import { i18n } from '@/i18n';
import { useAlertStore } from '@/modules/core/store/alert.store';
import { defineStore } from 'pinia';
import { ENUM_DeliveryQualityCheckTypes } from '../../common';

export interface DeliveryQualityCheckStoreState {
    items?: { key: string; value: DeliveryQualityCheckDetailDto[] }[];
    loading: boolean;
    reloading: boolean;
}

export const client = new DeliveryQualityCheckClient('', createInstance(), dateReviver);

export const useDeliveryQualityCheckStore = defineStore('configuration-delivery-quality-check-store', {
    state: (): DeliveryQualityCheckStoreState => ({
        items: [],
        loading: false,
        reloading: false
    }),
    getters: {
        bySpecification: (state) => {
            return (specificationId: number): DeliveryQualityCheckDetailDto[] => {
                const key = `specification-${specificationId}`;

                return state.items.find((x) => x.key === key)?.value ?? [];
            };
        }
    },
    actions: {
        async getBySpecification(specificationId: number, reloading: boolean = false) {
            const key = `specification-${specificationId}`;
            reloading ? (this.reloading = true) : (this.loading = true);
            try {
                const response = await client.getBySpecification(specificationId);
                const items = this.items.filter((x) => x.key !== key);
                if (response?.length) {
                    this.items = [...items, { key, value: response }];
                } else {
                    this.items = [...items];
                }
            } catch (error) {
                useAlertStore().handleException(
                    i18n.global.t('deliveryqualitycheck.detail.error.get.title'),
                    error,
                    false
                );
            } finally {
                reloading ? (this.reloading = false) : (this.loading = false);
            }
        },
        async saveBasic(specificationId: number, qualityCheckDto: SaveBasicQualityCheckDto): Promise<boolean> {
            let hasError = false;
            this.reloading = true;
            try {
                await client.saveBasicQualityCheck(specificationId, qualityCheckDto);
                useAlertStore().setSuccess(i18n.global.t('deliveryqualitycheck.detail.success.save.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryqualitycheck.detail.error.save.title'), error);
                this.reloading = false;
            }
            return !hasError;
        },
        async saveStandardStatus(
            specificationId: number,
            qualityCheckDto: SaveStandardQualityCheckStatusDto
        ): Promise<boolean> {
            let hasError = false;
            this.reloading = true;
            try {
                await client.saveStandardQualityCheckStatus(specificationId, qualityCheckDto);
                useAlertStore().setSuccess(i18n.global.t('deliveryqualitycheck.detail.success.save.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryqualitycheck.detail.error.save.title'), error);
                this.reloading = false;
            }
            return !hasError;
        },
        async insertStandard(specificationId: number, qualityCheckDto: SaveStandardQualityCheckDto): Promise<boolean> {
            let hasError = false;
            this.reloading = true;
            try {
                await client.insertStandardQualityCheck(specificationId, qualityCheckDto);
                useAlertStore().setSuccess(i18n.global.t('deliveryqualitycheck.detail.success.save.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryqualitycheck.detail.error.save.title'), error);
                this.reloading = false;
            }
            return !hasError;
        },
        async updateStandard(specificationId: number, qualityCheckDto: SaveStandardQualityCheckDto): Promise<boolean> {
            let hasError = false;
            this.reloading = true;
            try {
                await client.updateStandardQualityCheck(specificationId, qualityCheckDto);
                useAlertStore().setSuccess(i18n.global.t('deliveryqualitycheck.detail.success.save.title'));
                this.getBySpecification(specificationId, true);
            } catch (error) {
                hasError = true;
                useAlertStore().handleException(i18n.global.t('deliveryqualitycheck.detail.error.save.title'), error);
                this.reloading = false;
            }
            return !hasError;
        },
        async delete(specificationId: number, qualityCheckDto: DeleteQualityCheckDto): Promise<boolean> {
            this.reloading = true;
            try {
                await client.delete(specificationId, qualityCheckDto);
                useAlertStore().setSuccess(i18n.global.t('deliveryqualitycheck.detail.success.delete.title'));
                this.getBySpecification(specificationId, true);
                return true;
            } catch (error) {
                useAlertStore().handleException(i18n.global.t('deliveryqualitycheck.detail.error.delete.title'), error);
            } finally {
                this.reloading = false;
            }
            return false;
        },
        clear(specificationId: number) {
            const key = `specification-${specificationId}`;
            this.items = this.items.filter((x) => x.key !== key);
        }
    }
});
